/**
 * A lot's bid history, from the engine (2026-10-11).
 *
 * WHY. On a sale the engine runs, Postgres receives a lot's bids only when the
 * lot CLOSES (record at close, 2026-10-08). `get_lot_bid_history` therefore
 * answers an OPEN lot with nothing — a bidder who opens a lot mid-sale saw an
 * empty history until bids arrived on the feed while they watched. The engine
 * holds the trail, and serves it at `GET /v1/auction/:id/lots/:lotId/history`
 * with the house's privacy switches applied exactly as on the feed, and
 * `mine` on each bid for a signed-in bidder.
 *
 * Rows come out as the feed's do (`historyEntryFromEvent`), so a page merges
 * them with the database's (`mergeBidEntries`) and with live `event`s by the
 * same rules. ONE implementation, the website's and the app's.
 *
 * `notOnEngine`: the engine does not hold this sale (404) — read the database
 * instead; that is where a finished sale's bids live.
 */
import type { EventMessage } from '@auctisync/engine-wire';

import { historyEntryFromEvent, type LiveBidHistoryEntry } from './live-overlay.js';

export interface EngineHistoryRoute {
  engineUrl: string;
  engineAuctionId: string;
  houseId: string;
}

export interface EngineLotHistory {
  entries: LiveBidHistoryEntry[];
  /** The house hides its bid history from this viewer — say so, not "no bids". */
  hiddenByHouse: boolean;
  /** The engine does not hold this sale: the database is the place to read. */
  notOnEngine: boolean;
  error: string | null;
}

/** The engine's own page size. */
export const ENGINE_HISTORY_LIMIT = 50;

export async function fetchEngineLotHistory(
  route: EngineHistoryRoute,
  lotId: string,
  options: {
    /** The signed-in viewer's token now, or null signed out (a public read). */
    getToken: () => Promise<string | null>;
    limit?: number;
    fetch?: typeof fetch;
  },
): Promise<EngineLotHistory> {
  const doFetch = options.fetch ?? fetch;
  const limit = Math.min(Math.max(1, options.limit ?? ENGINE_HISTORY_LIMIT), 100);
  const url =
    `${route.engineUrl}/v1/auction/${encodeURIComponent(route.engineAuctionId)}` +
    `/lots/${encodeURIComponent(lotId)}/history?limit=${String(limit)}`;
  const token = await options.getToken();
  const headers: Record<string, string> = { 'X-House-Id': route.houseId };
  if (token !== null) headers.Authorization = `Bearer ${token}`;

  const empty = (over: Partial<EngineLotHistory>): EngineLotHistory => ({
    entries: [],
    hiddenByHouse: false,
    notOnEngine: false,
    error: null,
    ...over,
  });

  let res: Response;
  try {
    res = await doFetch(url, { headers });
  } catch (cause) {
    return empty({
      error: cause instanceof Error ? cause.message : 'The auction did not respond.',
    });
  }
  const body: unknown = await res.json().catch(() => null);
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  if (res.status === 404 && record.error === 'auction_not_published') {
    return empty({ notOnEngine: true });
  }
  if (!res.ok) {
    return empty({
      error: typeof record.error === 'string' ? record.error : `HTTP ${String(res.status)}`,
    });
  }
  const bids = Array.isArray(record.bids) ? (record.bids as EventMessage[]) : [];
  return empty({
    entries: bids
      .map((b) => historyEntryFromEvent(b))
      .filter((e): e is LiveBidHistoryEntry => e !== null),
    hiddenByHouse: record.hidden_by_house === true,
  });
}
